// hooks/useSettingsLogic.jsx
import { useState, useEffect } from "react";
import settingsApi from "services/api/settingsApi";

const useSettingsLogic = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    day: null,
    week: null,
    halfDayThreshold: null
  });
  const [editing, setEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Form state for day, week, and half-day settings
  const [formData, setFormData] = useState({
    activePeriod: 'day', // 'day' or 'week'
    day: {
      hours: 8
    },
    week: {
      hours: 40
    },
    halfDayThreshold: {
      hours: 4
    }
  });
  
  // Original data to track changes
  const [originalData, setOriginalData] = useState(null);
  
  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Fetch current settings from backend
  const fetchSettings = async () => {
    try {
      const res = await settingsApi.getSettings();
      if (res && res.length > 0) {
        // Separate day, week, and half-day settings
        const daySetting = res.find(setting => setting.label === "day Hours");
        const weekSetting = res.find(setting => setting.label === "week Hours");
        const halfDayThresholdSetting = res.find(setting => setting.label === "half_day_threshold");
        
        // Determine active period - if either permanent or contract is active for a period, consider it active
        let activePeriod = 'day';
        if (daySetting && (daySetting.permanent_active || daySetting.contract_active)) {
          activePeriod = 'day';
        } else if (weekSetting && (weekSetting.permanent_active || weekSetting.contract_active)) {
          activePeriod = 'week';
        }
        
        const data = {
          activePeriod,
          day: {
            hours: daySetting ? parseInt(daySetting.permanent_value) : 8
          },
          week: {
            hours: weekSetting ? parseInt(weekSetting.permanent_value) : 40
          },
          halfDayThreshold: {
            hours: halfDayThresholdSetting ? parseFloat(halfDayThresholdSetting.permanent_value) : 4
          }
        };
        
        setSettings({
          day: daySetting,
          week: weekSetting,
          halfDayThreshold: halfDayThresholdSetting
        });
        setFormData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // Deep copy for comparison
      } else {
        // No settings found for work category - initialize with defaults
        const defaultData = {
          activePeriod: 'day',
          day: {
            hours: 8
          },
          week: {
            hours: 40
          },
          halfDayThreshold: {
            hours: 4
          }
        };
        
        setFormData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
        showNotification("No settings found. Using default values.", "info");
      }
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to fetch settings", err);
      showNotification("Failed to load settings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Handle period change (day or week)
  const handlePeriodChange = (period) => {
    setFormData(prev => ({
      ...prev,
      activePeriod: period
    }));
    setHasUnsavedChanges(true);
  };

  // Handle hours change
  const handleHoursChange = (period, value) => {
    setFormData(prev => ({
      ...prev,
      [period]: {
        ...prev[period],
        hours: value === '' ? 0 : parseFloat(value) || 0
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Handle half-day threshold change
  const handleHalfDayThresholdChange = (value) => {
    setFormData(prev => ({
      ...prev,
      halfDayThreshold: {
        ...prev.halfDayThreshold,
        hours: value === '' ? 0 : parseFloat(value) || 0
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Initialize work hours setting
  const initializeWorkHoursSetting = async () => {
    try {
      const response = await settingsApi.initializeWorkHours();
      showNotification(response.message || "Work hours settings initialized successfully", "success");
      fetchSettings(); // Refresh the settings after initialization
    } catch (err) {
      console.error("Failed to initialize work hours setting", err);
      showNotification("Failed to initialize work hours setting", "error");
    }
  };

  // Save settings to backend
  const handleSaveSettings = async () => {
    if (!settings.day && !settings.week && !settings.halfDayThreshold) {
      showNotification("No settings available to update", "error");
      return;
    }

    try {
      const promises = [];
      
      // Update day setting if it exists
      if (settings.day) {
        // Check if active status changed
        const wasDayActive = originalData.activePeriod === 'day';
        const isDayActive = formData.activePeriod === 'day';
        
        if (wasDayActive !== isDayActive) {
          // Toggle both permanent and contract for day
          if (wasDayActive) {
            // Day was active, now inactive - toggle both to false
            if (settings.day.permanent_active) {
              promises.push(settingsApi.toggleSetting(settings.day.id, "permanent"));
            }
            if (settings.day.contract_active) {
              promises.push(settingsApi.toggleSetting(settings.day.id, "contract"));
            }
          } else {
            // Day was inactive, now active - toggle both to true
            if (!settings.day.permanent_active) {
              promises.push(settingsApi.toggleSetting(settings.day.id, "permanent"));
            }
            if (!settings.day.contract_active) {
              promises.push(settingsApi.toggleSetting(settings.day.id, "contract"));
            }
          }
        }
        
        // Check if hours changed
        if (formData.day.hours !== originalData.day.hours) {
          promises.push(
            settingsApi.updateSettingValue(settings.day.id, "permanent", formData.day.hours.toString())
          );
          promises.push(
            settingsApi.updateSettingValue(settings.day.id, "contract", formData.day.hours.toString())
          );
        }
      }
      
      // Update week setting if it exists
      if (settings.week) {
        // Check if active status changed
        const wasWeekActive = originalData.activePeriod === 'week';
        const isWeekActive = formData.activePeriod === 'week';
        
        if (wasWeekActive !== isWeekActive) {
          // Toggle both permanent and contract for week
          if (wasWeekActive) {
            // Week was active, now inactive - toggle both to false
            if (settings.week.permanent_active) {
              promises.push(settingsApi.toggleSetting(settings.week.id, "permanent"));
            }
            if (settings.week.contract_active) {
              promises.push(settingsApi.toggleSetting(settings.week.id, "contract"));
            }
          } else {
            // Week was inactive, now active - toggle both to true
            if (!settings.week.permanent_active) {
              promises.push(settingsApi.toggleSetting(settings.week.id, "permanent"));
            }
            if (!settings.week.contract_active) {
              promises.push(settingsApi.toggleSetting(settings.week.id, "contract"));
            }
          }
        }
        
        // Check if hours changed
        if (formData.week.hours !== originalData.week.hours) {
          promises.push(
            settingsApi.updateSettingValue(settings.week.id, "permanent", formData.week.hours.toString())
          );
          promises.push(
            settingsApi.updateSettingValue(settings.week.id, "contract", formData.week.hours.toString())
          );
        }
      }
      
      // Update half-day threshold setting if it exists
      if (settings.halfDayThreshold) {
        // Check if value changed
        if (formData.halfDayThreshold.hours !== originalData.halfDayThreshold.hours) {
          promises.push(
            settingsApi.updateSettingValue(settings.halfDayThreshold.id, "permanent", formData.halfDayThreshold.hours.toString())
          );
          promises.push(
            settingsApi.updateSettingValue(settings.halfDayThreshold.id, "contract", formData.halfDayThreshold.hours.toString())
          );
        }
      }
      
      if (promises.length === 0) {
        showNotification("No changes to save", "info");
        setEditing(false);
        return;
      }
      
      await Promise.all(promises);
      setHasUnsavedChanges(false);
      setEditing(false);
      fetchSettings(); // Refresh data
      showNotification("Settings saved successfully", "success");
    } catch (err) {
      console.error("Failed to save settings", err);
      showNotification("Failed to save settings", "error");
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    if (originalData) {
      setFormData(JSON.parse(JSON.stringify(originalData)));
    }
    setEditing(false);
    setHasUnsavedChanges(false);
    showNotification("Changes discarded", "info");
  };

  return {
    // State
    loading,
    settings,
    editing,
    hasUnsavedChanges,
    notification,
    formData,
    
    // Actions
    setEditing,
    showNotification,
    closeNotification,
    fetchSettings,
    handlePeriodChange,
    handleHoursChange,
    handleHalfDayThresholdChange,
    initializeWorkHoursSetting,
    handleSaveSettings,
    handleCancelEdit
  };
};

export default useSettingsLogic;